import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import config from "../config";
import { createSales } from "../api/salesApi";
import { createPaymentMethod } from "../api/paymentMethodApi";
import { useAuth } from "../context/AuthContext";

import SalesInvoice from "../components/Modal/SalesInvoice";

const PaymentCallbackSimple = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [paymentData, setPaymentData] = useState({});
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const { clientData, refreshClient } = useAuth();

  const currentSubRaw = localStorage.getItem("mySubscription");
  const currentSub = currentSubRaw ? JSON.parse(currentSubRaw) : null;
  const actionType = localStorage.getItem("actionType");
  const couponData = localStorage.getItem("coupon");
  const coupon = couponData ? JSON.parse(couponData) : null;
  const client = localStorage.getItem("client");
  const client_id = clientData
    ? clientData.client_id
    : JSON.parse(client).client_id;
  const [Sales, setSales] = useState(null);
  const [showIncoice, setShowInvoice] = useState(false);

  useEffect(() => {
    const params = {};
    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }
    console.log("Callback parameters:", params);
    setPaymentData(params);
    setTimeout(() => {
      if (params.signature && params.signed_field_names) {
        verifyPayment(params);
      } else {
        setLoading(false);
      }
    }, 0);
  }, [searchParams]);

  const verifyPayment = async (data) => {
    try {
      const response = await fetch(
        `${config.apiUrl}/verify-payment-signature`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );

      const result = await response.json();
      setVerified(result.isValid);

      if (result.isValid && data.decision === "ACCEPT") {
        // Process successful payment
        await processPayment(data);
      }
    } catch (error) {
      console.error("Verification error:", error);
    } finally {
      setLoading(false);
      if (client) {
        refreshClient(client_id);
        console.log("Client data refreshed.", client_id);
      }
    }
  };

  const processPayment = async (data) => {
    try {
      let discount = 0;

      if (coupon) {
        if (coupon.discount_type === "percentage") {
          discount = (data.auth_amount * coupon.discount_value) / 100;
        } else {
          discount = coupon.discount_value;
        }
      }
      console.log("Parsed subscription from localStorage:", currentSub);

      const response = await fetch(`${config.apiUrl}/process-payment-result`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          transactionId: data.transaction_id,
          decision: data.decision,
          authCode: data.auth_code,
          amount: data.auth_amount,
          duration: currentSub?.duration_days,
          referenceNumber: data.req_reference_number,
          payment_method: data.req_payment_method || null,
          card_type: data.req_card_type || null,
          masked_card_number: data.req_card_number || null,
          network_transaction_id: data?.req_transaction_uuid || null,
          message: data?.message || null,
        }),
      });

      const result = await response.json();
      console.log("Payment processing result:", result);
      //Create Sales

      if (result.success && result.status === "completed") {
        // 4. Prepare sales data
        const salesData = {
          client_subscription_id: currentSub.client_subscription_id,
          client_id: currentSub.client_id,
          price: data.auth_amount,
          payment_type: "ONLINE",
          action: actionType || "SUBSCRIBE",
          coupon_id: coupon?.coupon_id || null,
          discount: discount,
          total: data.auth_amount,
        };

        // 5. Call your createSales function (assumes it's imported or defined)
        const newSales = await createSales(salesData);
        console.log("Sales record created:", newSales);
        setSales(newSales);
        if (data.payment_token && data.payment_token_instrument_identifier_id) {
          console.log("Data:", data);
          const paymentMethodData = {
            client_id: currentSub.client_id,

            payment_token: data.payment_token,
            instrument_id: data.payment_token_instrument_identifier_id,

            bill_to_country: data.req_bill_to_address_country, // Should be 2-letter ISO code
            bill_to_address: data.req_bill_to_address_line1,
            bill_to_city: data.req_bill_to_address_city,
            bill_to_email: data.req_bill_to_email,
            bill_to_mobile: data.req_bill_to_phone,

            firstname: data.req_bill_to_forename,
            lastname: data.req_bill_to_surname,

            card_number: data.req_card_number, // mask all but last 4
            card_type_name: data.card_type_name, // e.g., 'Visa'
            card_type_number: data.req_card_type, // e.g., '001'

            payment_method: data.req_payment_method || "card", // fallback to 'card' if not defined
            status: "active",
          };

          try {
            const newPaymentMethod = await createPaymentMethod(
              paymentMethodData
            );
            console.log("Payment method saved:", newPaymentMethod);
          } catch (err) {
            console.error("Failed to save payment method:", err);
          }
        }
        // 6. Clear localStorage
        localStorage.removeItem("mySubscription");
        localStorage.removeItem("actionType");
        localStorage.removeItem("coupon");
      } else {
        console.warn("Payment not marked as completed:", result);
      }
    } catch (error) {
      console.error("Processing error:", error);
    }
  };

  const getStatusDisplay = () => {
    const decision = paymentData.decision;

    if (decision === "ACCEPT") {
      return {
        icon: "✅",
        title: "Payment Successful!",
        message: "Your payment has been processed successfully.",
        color: "text-green-600",
        bgColor: "bg-green-50",
        buttonText: "Continue",
        buttonColor: "bg-green-600 hover:bg-green-700",
      };
    } else if (decision === "DECLINE") {
      return {
        icon: "❌",
        title: "Payment Declined",
        message:
          "Your payment was declined. Please try a different payment method.",
        color: "text-red-600",
        bgColor: "bg-red-50",
        buttonText: "Try Again",
        buttonColor: "bg-red-600 hover:bg-red-700",
      };
    } else if (decision === "REVIEW") {
      return {
        icon: "⏳",
        title: "Payment Under Review",
        message:
          "Your payment is being reviewed. We will notify you once complete.",
        color: "text-yellow-600",
        bgColor: "bg-yellow-50",
        buttonText: "Continue",
        buttonColor: "bg-yellow-600 hover:bg-yellow-700",
      };
    } else if (decision === "CANCEL") {
      return {
        icon: "❌",
        title: "Payment CANCEL",
        message: "Your payment was canceled.",
        color: "text-yellow-600",
        bgColor: "bg-yellow-50",
        buttonText: "Continue",
        buttonColor: "bg-yellow-600 hover:bg-yellow-700",
      };
    } else if (decision === "ERROR") {
      return {
        icon: "❌",
        title: "Payment ERROR",
        message: "Your payment was declined",
        color: "text-yellow-600",
        bgColor: "bg-yellow-50",
        buttonText: "Continue",
        buttonColor: "bg-yellow-600 hover:bg-yellow-700",
      };
    } else {
      return {
        icon: "❓",
        title: "Payment Status Unknown",
        message: "Unable to determine payment status.",
        color: "text-gray-600",
        bgColor: "bg-gray-50",
        buttonText: "Continue",
        buttonColor: "bg-gray-600 hover:bg-gray-700",
      };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Verifying payment...</p>
        </div>
      </div>
    );
  }

  const status = getStatusDisplay();

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4">
        <div className={`${status.bgColor} rounded-lg p-8 shadow-lg`}>
          {/* Status Header */}
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">{status.icon}</div>
            <h1 className={`text-3xl font-bold ${status.color} mb-2`}>
              {status.title}
            </h1>
            <p className="text-gray-600 text-lg">{status.message}</p>

            {!verified && paymentData.signature && (
              <div className="mt-4 text-red-600">
                ⚠️ Payment signature could not be verified
              </div>
            )}
          </div>

          {/* Payment Details */}
          {Object.keys(paymentData).length > 0 && (
            <div className="bg-white rounded-lg p-6 mb-6">
              <h2 className="text-xl font-semibold mb-4">Payment Details</h2>
              <div className="space-y-3">
                {paymentData.transaction_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Transaction ID:</span>
                    <span className="font-medium">
                      {paymentData.transaction_id}
                    </span>
                  </div>
                )}

                {paymentData.req_reference_number && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Reference:</span>
                    <span className="font-medium">
                      {paymentData.req_reference_number}
                    </span>
                  </div>
                )}

                {paymentData.auth_amount && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-medium">
                      {paymentData.auth_amount} {paymentData.req_currency}
                    </span>
                  </div>
                )}

                {paymentData.auth_code && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Auth Code:</span>
                    <span className="font-medium">{paymentData.auth_code}</span>
                  </div>
                )}

                {paymentData.card_type_name && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Card Type:</span>
                    <span className="font-medium">
                      {paymentData.card_type_name}
                    </span>
                  </div>
                )}

                {paymentData.req_card_number && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Card Number:</span>
                    <span className="font-medium">
                      {paymentData.req_card_number}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error Details for Declined Payments */}
          {paymentData.decision === "DECLINE" && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-red-800 mb-2">
                Decline Information
              </h3>
              {paymentData.reason_code && (
                <p className="text-red-600">
                  Reason Code: {paymentData.reason_code}
                </p>
              )}
              {paymentData.message && (
                <p className="text-red-600">{paymentData.message}</p>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
            <button
              onClick={() => {
                if (paymentData.decision === "ACCEPT") {
                  navigate("/"); // or wherever you want to redirect after success
                } else {
                  navigate("/payment"); // back to payment page
                }
              }}
              className={`inline-flex items-center gap-3 px-6 py-3 rounded-lg text-white font-semibold shadow-md ${status.buttonColor} transition-transform transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-300`}
              aria-label={status.buttonText}
              title={status.buttonText}
            >
              {/* Success / Continue Icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.707a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{status.buttonText}</span>
            </button>

            <button
              onClick={() => setShowInvoice(true)}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-blue-700 bg-white border border-blue-200 hover:bg-blue-50 hover:shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-200"
              aria-label="View Invoice"
              title="View Invoice"
            >
              {/* Invoice / Receipt Icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 10V6a2 2 0 00-2-2H7L5 6v14l2-2h12a2 2 0 002-2v-4" />
                <path d="M8 6v2M12 6v2M16 6v2M8 13h8M8 17h5" />
              </svg>
              <span>View Invoice</span>
            </button>
          </div>

          {showIncoice && (
            <SalesInvoice
              salesToFetch={Sales}
              onClose={() => setShowInvoice(false)}
            />
          )}
          {process.env.NODE_ENV === "development" && (
            <details className="mt-8">
              <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                Debug: Raw Callback Data
              </summary>
              <pre className="mt-4 p-4 bg-gray-100 rounded text-xs overflow-auto max-h-96">
                {JSON.stringify(paymentData, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentCallbackSimple;
